import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import './UsersTopbar.css';
import {
  FaBell,
  FaChalkboardTeacher,
  FaSearch,
  FaBookOpen,
  FaShoppingCart,
  FaHeart,
  FaUserEdit,
  FaCog,
  FaCreditCard,
  FaStar,
  FaCommentDots,
  FaSignOutAlt,
  FaHome,
  FaCertificate,
  FaGift,
  FaInfoCircle,
} from 'react-icons/fa';
import defaultProfilePic from '../../assets/defaultProfilePic.png';
import logo from '../../assets/logo.png'; // Import your logo image here
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import { getProfilePictureUrl } from '../../utils/apiHelper';
import { FaBars } from 'react-icons/fa';
import axios from 'axios';
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook
import { useSocket } from '../../context/SocketContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCart } from '../../context/CartContext';
import Translate from '../../components/Translate/Translate';
import { FaGlobe } from 'react-icons/fa';

const UserTopbar = () => {
  useBlockCheck('user'); // 🔥 Prevents blocked users from accessing this page
  const { logout, profile, profileLoading, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userName, setUserName] = useState('Learner');
  const [profilePicture, setProfilePicture] = useState();
  const [courses, setCourses] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCategories, setShowCategories] = useState(false); // State for toggling categories
  const dropdownRef = useRef(null);
  const { socket } = useSocket();
  const { notificationCount, notifications, markAsRead } = useNotifications();
  const { cartCount } = useCart();
  const translateRef = useRef(null);
  const globeIconRef = useRef(null);

  // Function to position language menu
  const positionLanguageMenu = useCallback(() => {
    if (globeIconRef.current) {
      const rect = globeIconRef.current.getBoundingClientRect();
      // Menu is now in document.body via portal, so search there
      const menuElement = document.querySelector('body > .translate-mobile-menu') ||
                         document.querySelector('.translate-mobile-menu');
      if (menuElement) {
        const isMobile = window.innerWidth <= 1000;
        
        if (isMobile) {
          // Mobile/Tablet: position upwards and align with globe icon
          menuElement.style.top = `${Math.max(10, rect.top - 10)}px`;
          menuElement.style.bottom = 'auto';
          menuElement.style.transform = 'translateY(-100%)';
          // Align left edge with globe icon position
          menuElement.style.left = `${rect.left}px`;
          menuElement.style.right = 'auto';
        } else {
          // Desktop: position downwards
          menuElement.style.top = `${rect.bottom + 10}px`;
          menuElement.style.bottom = 'auto';
          menuElement.style.transform = 'none';
          menuElement.style.right = `${Math.max(10, window.innerWidth - rect.right)}px`;
          menuElement.style.left = 'auto';
        }
      }
    }
  }, []);

  // Watch for menu opening and position it correctly (menu is now in document.body)
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const menuElement = document.querySelector('body > .translate-mobile-menu');
      if (menuElement) {
        positionLanguageMenu();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [positionLanguageMenu]);

  // Update menu position on window resize
  useEffect(() => {
    const handleResize = () => {
      const menuElement = document.querySelector('body > .translate-mobile-menu');
      if (menuElement) {
        positionLanguageMenu();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [positionLanguageMenu]);

  useEffect(() => {
    if (!profileLoading && profile) {
      const name = profile.name || 'Learner';
      setUserName(name);
      setProfilePicture(
        getProfilePictureUrl(profile.profilePicture, api.img, defaultProfilePic, name),
      );
    }
  }, [profile, profileLoading]);

  useEffect(() => {
    // Close dropdown if clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // const fetchNotificationCount = useCallback(async () => {
  //   if (!token) {
  //     setNotificationCount(0);
  //     return;
  //   }
  //   try {
  //     const response = await axios.get(api.getUserNotifications, {
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });
  //     const notifications = response.data.notifications || [];
  //     const unreadCount = notifications.filter((notif) => !notif.read).length;
  //     setNotificationCount(unreadCount);
  //   } catch (error) {
  //     console.error("Error fetching notifications count:", error);
  //     setNotificationCount(0);
  //   }
  // }, [token]);

  //   useEffect(() => {
  //     fetchNotificationCount();
  //   }, [fetchNotificationCount]);

  useEffect(() => {
    if (!socket) return;

    // Gift notification
    const handleNewGiftNotification = () => {
      // No need to manually increment notificationCount here
    };
    socket.on('newGiftNotification', handleNewGiftNotification);

    // Course purchase notification
    const handleCoursePurchaseNotification = () => {
      // No need to manually increment notificationCount here
    };
    socket.on('newCoursePurchaseNotification', handleCoursePurchaseNotification);

    // Course update notification
    const handleCourseUpdateNotification = () => {
      // No need to manually increment notificationCount here
    };
    socket.on('newCourseUpdateNotification', handleCourseUpdateNotification);

    // Cleanup
    return () => {
      socket.off('newGiftNotification', handleNewGiftNotification);
      socket.off('newCoursePurchaseNotification', handleCoursePurchaseNotification);
      socket.off('newCourseUpdateNotification', handleCourseUpdateNotification);
    };
  }, [socket]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleInstructorClick = () => {
    // Redirect to the instructor portal
    window.location.href = '/dashboard';
  };

  return (
    <div className="users-home-container">
      <header className="users-homenavbar">
        {/* Home button for mobile responsive - only visible on mobile */}
        <button
          className="users-mobile-home-button"
          onClick={() => navigate('/home')}
          title="Home"
        >
          <FaHome className="users-mobile-home-icon" />
        </button>
        
        <Link to="/">
          <img
            src={logo}
            alt="UlsaApp Logo"
            className="users-homelogo-img"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              navigate('/home');
              window.location.href = '/home'; // Force reload
            }}
          />
        </Link>

        {/* Language selector - hidden but functional */}
        <div className="users-translate-hidden">
          <Translate ref={translateRef} />
        </div>

        <div className="users-navbar-right-container">
          <div className="users-navbar-icons">
            {/* Language/Globe icon - replaces home icon */}
            <div
              ref={globeIconRef}
              className="users-globe-icon-wrapper"
              onClick={() => {
                if (translateRef.current) {
                  translateRef.current.openMenu();
                  // Position menu after it's rendered via portal (in document.body)
                  setTimeout(() => {
                    positionLanguageMenu();
                  }, 10);
                  setTimeout(() => {
                    positionLanguageMenu();
                  }, 50);
                  setTimeout(() => {
                    positionLanguageMenu();
                  }, 100);
                } else {
                  console.error('translateRef.current is null');
                }
              }}
            >
              <FaGlobe
                className="users-icon users-globe-icon"
                title="Select Language"
              />
            </div>
            <FaBookOpen
              className={`users-icon ${window.location.pathname === '/learning' ? 'active' : ''}`}
              title="My Learning"
              onClick={() => navigate('/learning')}
            />
            <FaHeart
              className={`users-icon ${window.location.pathname === '/wishlist' ? 'active' : ''}`}
              title="My Wishlist"
              onClick={() => navigate('/wishlist')}
            />
            <div className="cart-icon-container">
              <FaShoppingCart
                className={`users-icon ${window.location.pathname === '/cart' ? 'active' : ''}`}
                title="My Cart"
                onClick={() => navigate('/cart')}
              />
              {cartCount > 0 && (
                <span className="cart-badge">{cartCount}</span>
              )}
            </div>
            <FaChalkboardTeacher
              className="instructor-alt"
              title="Instructor"
              onClick={handleInstructorClick}
            />

            <div className={`users-notification-icon-container ${location.pathname === '/notifications' ? 'active' : ''}`}>
              <FaBell
                className="icon"
                title="Notifications"
                onClick={() => {
                  navigate('/notifications');
                }}
              />
              {notificationCount > 0 && (
                <span className="usertp-notification-badge">{notificationCount}</span>
              )}
            </div>

          </div>
          <div
            className="users-profile-icon-container"
            title="Profile"
            ref={dropdownRef}
          >
          <img
            src={profilePicture}
            alt="profile picture"
            className="users-profile-icon"
            onClick={() => setShowDropdown(!showDropdown)}
          />
          {showDropdown && (
            <div className="users-dropdown-menu">
              <div className="users-profile-info">
                <img
                  src={profilePicture}
                  alt="profile picture"
                  className="users-dropdown-profile-icon"
                />
                <p className="users-dropdown-item">{userName}</p>
              </div>
              {/* {!location.pathname.startsWith('/learning') && (
                <button
                  className="users-menu-item"
                  onClick={() => navigate("/learning")}
                >
                  <FaBookOpen className="users-menu-item-icon" /> My Learning
                </button>
              )}
              {!location.pathname.startsWith('/cart') && (
                <button
                  className="users-menu-item"
                  onClick={() => navigate("/cart")}
                >
                  <FaShoppingCart className="users-menu-item-icon" /> Cart
                </button>
              )}
              {!location.pathname.startsWith('/wishlist') && (
                <button
                  className="users-menu-item"
                  onClick={() => navigate("/wishlist")}
                >
                  <FaHeart className="users-menu-item-icon" /> Wishlist
                </button>
              )}
              {!location.pathname.startsWith('/notifications') && (
                <button
                  className="menu-item"
                  onClick={() => navigate("/notifications")}
                >
                  <FaBell className="users-menu-item-icon" />
                  Notifications
                </button>
              )} */}
              {!location.pathname.startsWith('/edit-profile') && (
                <button
                  className="users-menu-item"
                  onClick={() => navigate('/edit-profile')}
                >
                  <FaUserEdit className="users-menu-item-icon" /> Edit Profile
                </button>
              )}
              {!location.pathname.startsWith('/account-settings') && (
                <button
                  className="users-menu-item"
                  onClick={() => navigate('/account-settings')}
                >
                  <FaCog className="users-menu-item-icon" /> Account Settings
                </button>
              )}
              {/* <button
                className="users-menu-item"
                onClick={() => navigate("/payment-method")}
              >
                <FaCreditCard className="users-menu-item-icon" /> Payment
                Method
              </button>
              <button
                className="users-menu-item"
                onClick={() => navigate("/subscriptions")}
              >
                <FaStar className="users-menu-item-icon" /> Subscriptions
              </button> */}
              {!location.pathname.startsWith('/users-feedback') && (
                <button
                  className="menu-item"
                  onClick={() => navigate('/users-feedback')}
                >
                  <FaCommentDots className="users-menu-item-icon" />Feedback
                </button>
              )}
              {!location.pathname.startsWith('/dashboard') && (
                <button
                  className="users-menu-item"
                  onClick={() => navigate('/dashboard')}
                >
                  <FaChalkboardTeacher className="users-menu-item-icon" /> Instructor
                </button>
              )}
              {!location.pathname.startsWith('/my-certificates') && (
                <button
                  className="menu-item"
                  onClick={() => navigate('/my-certificates')}
                >
                  <FaCertificate className="users-menu-item-icon" /> My Certificates
                </button>
              )}
              {!location.pathname.startsWith('/gifted') && (
                <button
                  className="menu-item"
                  onClick={() => navigate('/gifted')}
                >
                  <FaGift className="users-menu-item-icon" /> Gift Management
                </button>

              )}
              <button
                className="menu-item"
                onClick={() => navigate('/about')}
              >
                <FaInfoCircle className="menu-item-icon" /> About Us
              </button>
              <button className="users-logout-button" onClick={handleLogout}>
                <FaSignOutAlt className="users-menu-item-icon" /> Logout
              </button>
            </div>
          )}
          </div>
        </div>
      </header>
    </div>
  );
};

export default UserTopbar;
